import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, ExternalLink, FileCode, Check, Clock } from "lucide-react";
import WikiHeader from "./WikiHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WikiTemplates() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [username, setUsername] = useState("");
  const [templates, setTemplates] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: "", wikipedia_url: "", description: "" });
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("wiki_token");
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/wiki/verify?token=${token}`);
      if (response.data.valid) {
        setIsAuthenticated(true);
        setUsername(response.data.username);
        fetchTemplates(token);
      } else {
        localStorage.removeItem("wiki_token");
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  const fetchTemplates = async (token) => {
    try {
      const response = await axios.get(`${API}/wiki/templates?token=${token}`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTemplate = async (e) => {
    e.preventDefault();
    if (!newTemplate.name.trim()) {
      toast.error("Please enter a template name");
      return;
    }

    const token = localStorage.getItem("wiki_token");
    setIsAdding(true);

    try {
      const response = await axios.post(`${API}/wiki/templates?token=${token}`, {
        name: newTemplate.name.trim(),
        wikipedia_url: newTemplate.wikipedia_url.trim() || `https://en.wikipedia.org/wiki/Template:${newTemplate.name.trim()}`,
        description: newTemplate.description.trim()
      });

      toast.success(response.data.message || "Template added successfully");
      setShowAddModal(false);
      setNewTemplate({ name: "", wikipedia_url: "", description: "" });
      fetchTemplates(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add template");
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteTemplate = async (templateId, templateName) => {
    if (!confirm(`Are you sure you want to delete the template "${templateName}"?`)) {
      return;
    }

    const token = localStorage.getItem("wiki_token");
    try {
      await axios.delete(`${API}/wiki/templates/${templateId}?token=${token}`);
      toast.success("Template deleted");
      fetchTemplates(token);
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("wiki_token");
    setIsAuthenticated(false);
    toast.success("Logged out successfully");
    navigate("/wiki/login");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f6f6f6] flex items-center justify-center">
        <div className="text-[#72777d]">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#f6f6f6]">
        <WikiHeader username="" onLogout={() => {}} showSearch={false} />
        <div className="max-w-[800px] mx-auto mt-12 px-4">
          <div className="bg-white border border-[#a2a9b1] p-6">
            <h1 className="font-wiki text-2xl text-[#202122] mb-4 border-b border-[#a2a9b1] pb-3">
              Access Restricted
            </h1>
            <p className="text-[#202122]">
              Please <Link to="/wiki/login" className="text-[#3366cc] hover:underline">log in</Link> to manage templates.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f6f6]">
      <WikiHeader username={username} onLogout={handleLogout} showSearch={true} />

      {/* Content */}
      <div className="max-w-[1200px] mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Sidebar */}
          <aside className="w-[160px] flex-shrink-0">
            <nav className="text-sm">
              <div className="mb-4">
                <h3 className="font-bold text-[#202122] mb-2">Navigation</h3>
                <ul className="space-y-1">
                  <li><Link to="/wiki" className="text-[#3366cc] hover:underline">Main page</Link></li>
                  <li><Link to="/wiki/templates" className="text-[#202122] font-medium">Templates</Link></li>
                </ul>
              </div>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 bg-white border border-[#a2a9b1] p-6">
            <div className="flex items-center justify-between mb-6 border-b border-[#a2a9b1] pb-4">
              <h1 className="font-wiki text-2xl text-[#202122]">Wiki Templates</h1>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 bg-[#3366cc] text-white px-4 py-2 text-sm font-medium hover:bg-[#2a4b8d]"
              >
                <Plus className="w-4 h-4" />
                Add Template
              </button>
            </div>

            <p className="text-[#54595d] mb-6">
              Manage Wikipedia templates that are supported in this wiki. Add templates by providing their Wikipedia URL or name.
            </p>

            {/* Built-in Templates Section */}
            <div className="mb-8">
              <h2 className="text-lg font-bold text-[#202122] mb-3 flex items-center gap-2">
                <FileCode className="w-5 h-5" />
                Built-in Templates
              </h2>
              <div className="bg-[#f8f9fa] border border-[#a2a9b1] rounded p-4">
                <p className="text-sm text-[#54595d] mb-3">
                  These templates are natively supported and work automatically:
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  {[
                    { name: "Infobox", desc: "Information boxes" },
                    { name: "Reflist", desc: "Reference lists" },
                    { name: "Collapse top/bottom", desc: "Collapsible sections" },
                    { name: "Cite web", desc: "Web citations" },
                    { name: "URL", desc: "External URLs" },
                    { name: "Main", desc: "Main article links" },
                    { name: "See also", desc: "Related links" },
                    { name: "Citation needed", desc: "Citation markers" },
                    { name: "Convert", desc: "Unit conversions" },
                    { name: "As of", desc: "Date markers" },
                    { name: "Ubl / Unbulleted list", desc: "Unbulleted lists" },
                    { name: "Hlist", desc: "Horizontal lists" },
                    { name: "Plainlist", desc: "Plain lists" },
                    { name: "Flatlist", desc: "Flat inline lists" },
                  ].map((t) => (
                    <div key={t.name} className="flex items-start gap-2 p-2 bg-white rounded border border-[#c8ccd1]">
                      <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-mono text-[#202122]">{`{{${t.name}}}`}</span>
                        <p className="text-xs text-[#72777d]">{t.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Custom Templates Section */}
            <div>
              <h2 className="text-lg font-bold text-[#202122] mb-3">Custom Templates</h2>
              
              {templates.length === 0 ? (
                <div className="bg-[#f8f9fa] border border-[#a2a9b1] rounded p-8 text-center">
                  <FileCode className="w-12 h-12 text-[#a2a9b1] mx-auto mb-3" />
                  <p className="text-[#54595d]">No custom templates added yet.</p>
                  <p className="text-sm text-[#72777d] mt-1">
                    Click "Add Template" to import a template from Wikipedia.
                  </p>
                </div>
              ) : (
                <div className="border border-[#a2a9b1] rounded overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#eaecf0]">
                      <tr>
                        <th className="text-left p-3 font-medium text-[#202122]">Template</th>
                        <th className="text-left p-3 font-medium text-[#202122]">Description</th>
                        <th className="text-left p-3 font-medium text-[#202122]">Status</th>
                        <th className="text-left p-3 font-medium text-[#202122]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {templates.map((template) => (
                        <tr key={template.id} className="border-t border-[#c8ccd1] hover:bg-[#f8f9fa]">
                          <td className="p-3">
                            <span className="font-mono text-[#3366cc]">{`{{${template.display_name}}}`}</span>
                          </td>
                          <td className="p-3 text-[#54595d]">
                            {template.description || "No description"}
                          </td>
                          <td className="p-3">
                            {template.status === 'active' ? (
                              <span className="inline-flex items-center gap-1 text-green-700 bg-green-100 px-2 py-0.5 rounded text-xs">
                                <Check className="w-3 h-3" /> Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-100 px-2 py-0.5 rounded text-xs">
                                <Clock className="w-3 h-3" /> Pending
                              </span>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <a
                                href={template.wikipedia_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#3366cc] hover:underline flex items-center gap-1"
                              >
                                <ExternalLink className="w-3 h-3" />
                                View
                              </a>
                              <button
                                onClick={() => handleDeleteTemplate(template.id, template.display_name)}
                                className="text-red-600 hover:text-red-800 flex items-center gap-1"
                              >
                                <Trash2 className="w-3 h-3" />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Add Template Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-lg shadow-xl w-[500px] max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-[#a2a9b1]">
              <h3 className="font-bold text-lg text-[#202122]">Add Wikipedia Template</h3>
              <button onClick={() => setShowAddModal(false)} className="text-[#72777d] hover:text-[#202122]">
                ✕
              </button>
            </div>
            
            <form onSubmit={handleAddTemplate} className="p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#202122] mb-1">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  placeholder="e.g., Infobox person, Cite book, Navbox"
                  className="w-full border border-[#a2a9b1] rounded px-3 py-2 text-sm focus:border-[#3366cc] focus:outline-none"
                  autoFocus
                />
                <p className="text-xs text-[#72777d] mt-1">
                  Enter the template name without "Template:" prefix
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-[#202122] mb-1">
                  Wikipedia URL (optional)
                </label>
                <input
                  type="url"
                  value={newTemplate.wikipedia_url}
                  onChange={(e) => setNewTemplate({ ...newTemplate, wikipedia_url: e.target.value })}
                  placeholder="https://en.wikipedia.org/wiki/Template:Example"
                  className="w-full border border-[#a2a9b1] rounded px-3 py-2 text-sm focus:border-[#3366cc] focus:outline-none"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-[#202122] mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={newTemplate.description}
                  onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                  placeholder="Brief description of what this template does"
                  className="w-full border border-[#a2a9b1] rounded px-3 py-2 text-sm focus:border-[#3366cc] focus:outline-none h-20"
                />
              </div>

              <div className="bg-[#f8f9fa] -mx-4 -mb-4 p-4 mt-4 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-sm text-[#72777d] hover:text-[#202122]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isAdding}
                  className="px-4 py-2 bg-[#3366cc] text-white text-sm rounded hover:bg-[#2a4b8d] disabled:opacity-50"
                >
                  {isAdding ? "Adding..." : "Add Template"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
